'use client';

import { useCallback, useEffect } from 'react';
import { useSyncExternalStore } from 'react';
import { THEME_CANVAS } from './theme-canvas';

/**
 * The theme preference: an explicit choice, or `system` to follow the OS. This is
 * a pure client/UI preference (not domain state, so it never touches the event
 * log) - persisted in localStorage and applied to <html data-theme>, which is
 * what `globals.css` keys the dark palette on.
 *
 * The boot script in `layout.tsx` resolves and applies this same key *before*
 * first paint, so this hook only owns runtime changes and keeping `system` in
 * sync with the OS while the app is open. Keep the storage key and the resolve
 * rule here identical to that inline script.
 */
export type ThemePref = 'light' | 'dark' | 'system';

export const THEME_KEY = 'tesserano-theme';

const DARK_QUERY = '(prefers-color-scheme: dark)';

const listeners = new Set<() => void>();

function notify() {
  for (const l of listeners) l();
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  // Mirror changes made in another tab.
  const onStorage = (e: StorageEvent) => {
    if (e.key === THEME_KEY) cb();
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(cb);
    window.removeEventListener('storage', onStorage);
  };
}

function readPref(): ThemePref {
  try {
    const v = localStorage.getItem(THEME_KEY);
    if (v === 'light' || v === 'dark' || v === 'system') return v;
  } catch {
    /* private mode / disabled storage: fall through to the default */
  }
  return 'system';
}

function resolve(pref: ThemePref): 'light' | 'dark' {
  if (pref !== 'system') return pref;
  return window.matchMedia(DARK_QUERY).matches ? 'dark' : 'light';
}

// The browser chrome (address bar tint) follows the canvas via the
// theme-color meta; the colours are `THEME_CANVAS`, shared with the boot
// script in layout.tsx.

function applyToDom(pref: ThemePref): void {
  const mode = resolve(pref);
  document.documentElement.setAttribute('data-theme', mode);
  // Mutate-or-create the ONE theme-color meta (the boot script's pin in
  // layout.tsx), so a runtime toggle retints the browser chrome too. Never
  // remove meta nodes: the old remove-and-append deleted elements React
  // managed (the viewport export's SSR pair, since dropped) and React's next
  // commit crashed on a null removeChild.
  let meta = document.querySelector('meta[name="theme-color"]');
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', 'theme-color');
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', THEME_CANVAS[mode]);
}

/**
 * Read and set the theme preference. `pref` is the stored choice (light/dark/
 * system); `setPref` persists it, repaints the DOM, and notifies every other
 * `useTheme` consumer so all toggles stay in lock-step.
 */
export function useTheme(): { pref: ThemePref; setPref: (next: ThemePref) => void } {
  // Server render and the very first client render return `system` (the boot
  // script already painted the real theme on <html>); useSyncExternalStore then
  // reconciles to the stored value without a hydration warning.
  const pref = useSyncExternalStore(subscribe, readPref, () => 'system' as ThemePref);

  const setPref = useCallback((next: ThemePref) => {
    try {
      localStorage.setItem(THEME_KEY, next);
    } catch {
      /* storage unavailable: still apply for this session */
    }
    applyToDom(next);
    notify();
  }, []);

  // While following the system, track live OS theme flips (e.g. a sunset auto-switch).
  useEffect(() => {
    if (pref !== 'system') return;
    const mq = window.matchMedia(DARK_QUERY);
    const onChange = () => applyToDom('system');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [pref]);

  return { pref, setPref };
}
